import { Chat as StreamChat } from 'stream-chat-react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import useChatNotifications from '@features/components/Chat/components/hooks/useChatNotifications';
import { TxCenter } from '@features/components/Chat/types';
import { Spinner } from '@heroui/react';
import { useStreamChatFilters } from '@features/components/Chat/components/hooks/useStreamChatFilters';
import type { ChannelFilters } from 'stream-chat';
import { useParams } from 'react-router-dom';
import { StreamChannelType } from '@/api/apis/chat';
import { useGetStreamToken } from '@/hooks/useGetStreamToken';
import { useCreateStreamChat } from '@/hooks/useCreateStreamChat';
import { STREAM_API_KEY } from '@/constants/env.constants';
import { notificationAvailable } from '@/utils/helpers';

type ChatContextProps = {
  userId: string;
  currentStreamToken: string | null;
  chatGrants: StreamChannelType[] | null;
  chatTypes: StreamChannelType[];
  streamChatFilters: ChannelFilters | null;
  isStreamTokenLoading: boolean;
  isStreamTokenError: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isConnectionError: boolean;
  isOpoMode: boolean;
  connectToChat: () => void;
};

const DEFAULT_APP_CONTEXT: ChatContextProps = {
  userId: '',
  currentStreamToken: null,
  chatGrants: null,
  chatTypes: [],
  streamChatFilters: null,
  isStreamTokenLoading: false,
  isStreamTokenError: false,
  isConnected: false,
  isConnecting: false,
  isConnectionError: false,
  isOpoMode: false,
  connectToChat: () => {},
};

const ChatContext = createContext<ChatContextProps>(DEFAULT_APP_CONTEXT);

export const StreamChatNotifications = ({
  txCenters,
  children,
}: PropsWithChildren<{ txCenters?: TxCenter[] | null }>) => {
  useChatNotifications(txCenters);

  return children;
};

export const ChatProvider = ({
  apiKey,
  userId,
  txCenterId,
  txCenters,
  isOpoMode,
  children,
}: PropsWithChildren<{
  apiKey: string;
  userId: string;
  txCenterId?: string;
  txCenters?: TxCenter[] | null;
  isOpoMode?: boolean;
}>) => {
  const { caseId } = useParams<{ caseId: string }>();
  const { getValidStreamToken, currentStreamToken, chatGrants, isStreamTokenLoading, isStreamTokenError } =
    useGetStreamToken();

  const { chatClient, isConnected, isConnecting, isConnectionError, connectToChat } = useCreateStreamChat({
    apiKey,
    userData: { id: userId },
    tokenOrProvider: getValidStreamToken,
    options: {
      timeout: 10000,
    },
  });
  const { filters: streamChatFilters, chatTypes } = useStreamChatFilters({
    userId,
    caseId,
    userRole: chatClient?.user?.role ?? null,
    allowedTypes: chatGrants ?? undefined,
    txCenterId,
    isOpoMode,
  });

  const value = useMemo(
    () => ({
      userId,
      currentStreamToken,
      chatGrants,
      chatTypes,
      streamChatFilters,
      isStreamTokenLoading,
      isStreamTokenError,
      isConnecting,
      isConnected,
      isConnectionError,
      isOpoMode: isOpoMode ?? false,
      connectToChat,
    }),
    [
      userId,
      currentStreamToken,
      chatGrants,
      chatTypes,
      streamChatFilters,
      isStreamTokenLoading,
      isStreamTokenError,
      isConnecting,
      isConnected,
      isConnectionError,
      isOpoMode,
      connectToChat,
    ],
  );

  if (!chatClient) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  return (
    <ChatContext.Provider value={value}>
      <StreamChat client={chatClient} theme="messaging">
        {notificationAvailable() ? (
          <StreamChatNotifications txCenters={txCenters}>{children}</StreamChatNotifications>
        ) : (
          children
        )}
      </StreamChat>
    </ChatContext.Provider>
  );
};

type ChatProviderWrapperProps = PropsWithChildren<{
  userId: string;
  hasPermissions?: boolean;
}>;

export const ChatProviderWrapper = ({ userId, hasPermissions, children }: ChatProviderWrapperProps) => {
  if (!hasPermissions) {
    return children;
  }

  return (
    <ChatProvider apiKey={STREAM_API_KEY} userId={userId}>
      {children}
    </ChatProvider>
  );
};

export const useStreamChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useStreamChatContext must be used within an ChatProvider');
  }
  return context;
};
